import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useEffect, useState } from 'react';

import { RETRY_FAILED_QUERIES } from '@/config';
import { useAuth } from '@/context/appContext';
import { queryClientDisposeOnCacheEvict } from '@/utils/queryClientDisposeOnCacheEvict';

const QuerySession = ({ children }: { children: ReactNode }) => {
  const [client] = useState(() => {
    const client = new QueryClient({
      defaultOptions: { queries: { networkMode: 'always', retry: RETRY_FAILED_QUERIES ? 3 : false } },
    });
    queryClientDisposeOnCacheEvict(client);
    return client;
  });

  // clear() cancels queries and disposes cached values. Old mutation callbacks
  // can still settle, but can only write to this retired client.
  useEffect(() => () => client.clear(), [client]);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
};

/** A session's client and mounted views are its cache namespace, including Canada. */
export const SessionQueryProvider = ({ children }: { children: ReactNode }) => {
  const { authInfo } = useAuth();
  const sessionKey = JSON.stringify([authInfo?.reviewer_id, authInfo?.access_token]);
  return <QuerySession key={sessionKey}>{children}</QuerySession>;
};
